import { Injectable } from '@angular/core';
import { AiListResponse } from "../../../concierge-app/chatbot/models/ai-list-response";
import { ResultType } from "../../../concierge-app/chatbot/models/content-response-type";
import { ShopRecommendations } from "../../../concierge-app/chatbot/models/shop-recommendations";
import { union } from "lodash-es";
import { AnalyticsEventCategory, sendEvent } from "../../analytics/analytics";

@Injectable({
  providedIn: 'root'
})
export class ChatbotContextService {

  MAX_RECOMMENDED_SHOPS = 200;

  public updateRecommendedList(response: AiListResponse) {
    if (!response || !response.content) {
      return;
    }

    const shopRecommendations = response.content.find(item => item.resultType === ResultType.SHOP_RECOMMENDATIONS) as ShopRecommendations | undefined;

    if (!shopRecommendations) {
      return;
    }

    let savedRecommendations = localStorage.getItem(this.getRecommendShopLocalStorageKey());

    let currentParsedRecommendations: string[] = [];

    try {
      if (savedRecommendations) {
        currentParsedRecommendations = JSON.parse(savedRecommendations);
      }
    } catch {
      currentParsedRecommendations = [];
    }

    if (currentParsedRecommendations.length > this.MAX_RECOMMENDED_SHOPS) {
      // Very unlikely, but should monitor just in case.
      sendEvent(AnalyticsEventCategory.CHATBOT_TOO_MUCH_RECOMMENDATIONS, currentParsedRecommendations.length)
      currentParsedRecommendations = currentParsedRecommendations.slice(-this.MAX_RECOMMENDED_SHOPS);
    }

    const newRecommendations = shopRecommendations.shops.map(shop => shop.shopNumber);

    currentParsedRecommendations = union(currentParsedRecommendations, newRecommendations);

    localStorage.setItem(this.getRecommendShopLocalStorageKey(), JSON.stringify(currentParsedRecommendations));
  }

  public shopWasRecommended(shopNumber: string | null): boolean {
    if (!shopNumber) {
      return false;
    }

    const savedRecommendations = localStorage.getItem(this.getRecommendShopLocalStorageKey());

    if (!savedRecommendations) {
      return false;
    }

    try {
      const parsedRecommendations: string[] = JSON.parse(savedRecommendations);

      return parsedRecommendations.includes(shopNumber);
    } catch {
      return false;
    }
  }

  private getRecommendShopLocalStorageKey() {
    return 'chatbot-recommendation-shops';
  }

  public getSessionUserId(): string {
    let userId = sessionStorage.getItem('chatbot-session-user-id');
    if (!userId) {
      if (window.crypto && window.crypto?.randomUUID) {
        userId = window.crypto.randomUUID();
      } else {
        // Older browsers may not support crypto.randomUUID()
        userId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      }

      sessionStorage.setItem('chatbot-session-user-id', userId);
    }
    return userId;
  }

  public getPopularRestaurantOffset(): number {
    const sessionStorageKey = 'chatbot-session-restaurant-offset';
    const offset = sessionStorage.getItem(sessionStorageKey);

    if (offset) {
      const offsetToReturn = parseInt(offset);
      sessionStorage.setItem(sessionStorageKey, (offsetToReturn + 1).toString());
      return offsetToReturn;
    } else {
      sessionStorage.setItem(sessionStorageKey, "1");
      return 0;
    }
  }
}
